import { useState, useEffect } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./index.css";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import TopNotificationBar from "./components/TopNotificationBar";
import WhatsAppButton from "./components/WhatsAppButton";
import ChatbotWidget from "./components/ChatbotWidget";
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import ServicesPage from "./pages/ServicesPage";
import SolutionDetailPage from "./pages/SolutionDetailPage";
import ContactPage from "./pages/ContactPage";
import PartnersPage from "./pages/PartnersPage";
import ServiceDeskPage from "./pages/ServiceDeskPage";
import ServiceDeskAdminPage from "./pages/ServiceDeskAdminPage";
import IndiaCustomerMapPage from "./pages/IndiaCustomerMapPage";
import AwsActivatePage from "./pages/AwsActivatePage";
import BlogPage from "./pages/BlogPage";
import BlogDetailPage from "./pages/BlogDetailPage";
import CareersPage from "./pages/CareersPage";
import CareerDetailPage from "./pages/CareerDetailPage";
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminRequireAuth from "./pages/admin/AdminRequireAuth";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminBlogsPage from "./pages/admin/AdminBlogsPage";
import AdminBlogCategoriesPage from "./pages/admin/AdminBlogCategoriesPage";
import AdminJobsPage from "./pages/admin/AdminJobsPage";
import AdminJobCategoriesPage from "./pages/admin/AdminJobCategoriesPage";
import AdminApplicationsPage from "./pages/admin/AdminApplicationsPage";
import AdminSaleInquiriesPage from "./pages/admin/AdminSaleInquiriesPage";
import AdminAwsActivatePage from "./pages/admin/AdminAwsActivatePage";
import AdminContactInquiriesPage from "./pages/admin/AdminContactInquiriesPage";
import AdminPartnersPage from "./pages/admin/AdminPartnersPage";
import AdminPartnerCategoriesPage from "./pages/admin/AdminPartnerCategoriesPage";
import AdminServiceDeskPage from "./pages/admin/AdminServiceDeskPage";

export default function App() {
  const [showTopBar, setShowTopBar] = useState(true);
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  useEffect(() => {
    try {
      if (sessionStorage.getItem("emicron_topbar_dismissed")) setShowTopBar(false);
    } catch (_) {}
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  useEffect(() => {
    document.body.classList.remove("route-animating");
    void document.body.offsetWidth;
    document.body.classList.add("route-animating");
    const timer = window.setTimeout(() => {
      document.body.classList.remove("route-animating");
    }, 520);
    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    if (isAdminRoute) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("in-view");
        });
      },
      { rootMargin: "0px 0px -60px 0px", threshold: 0.05 }
    );
    const observeNew = () => {
      document.querySelectorAll(".animate-on-scroll:not(.in-view)").forEach((el) => {
        observer.observe(el);
      });
    };
    observeNew();
    const main = document.querySelector(".main-content");
    const mutationObserver = main
      ? new MutationObserver(() => observeNew())
      : null;
    mutationObserver?.observe(main, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      mutationObserver?.disconnect();
    };
  }, [location.pathname, isAdminRoute]);

  return (
    <div className={`${showTopBar && !isAdminRoute ? "layout-topbar-visible" : ""}${isAdminRoute ? " is-admin-app" : ""}`}>
      {!isAdminRoute && (
        <TopNotificationBar visible={showTopBar} onClose={() => setShowTopBar(false)} />
      )}
      {!isAdminRoute && <Navbar />}
      <main className={isAdminRoute ? "admin-app-main" : "main-content"}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/solutions/:slug" element={<SolutionDetailPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/customers" element={<IndiaCustomerMapPage />} />
          <Route path="/service-desk" element={<ServiceDeskPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/aws-activate" element={<AwsActivatePage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogDetailPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/careers/:slug" element={<CareerDetailPage />} />

          <Route path="/admin">
            <Route path="login" element={<AdminLoginPage />} />
            <Route element={<AdminRequireAuth />}>
              <Route index element={<AdminDashboard />} />
              <Route path="blogs" element={<AdminBlogsPage />} />
              <Route path="blog-categories" element={<AdminBlogCategoriesPage />} />
              <Route path="jobs" element={<AdminJobsPage />} />
              <Route path="job-categories" element={<AdminJobCategoriesPage />} />
              <Route path="applications" element={<AdminApplicationsPage />} />
              <Route path="contact-inquiries" element={<AdminContactInquiriesPage />} />
              <Route path="sale-inquiries" element={<AdminSaleInquiriesPage />} />
              <Route path="aws-activate" element={<AdminAwsActivatePage />} />
              <Route path="partners" element={<AdminPartnersPage />} />
              <Route path="partner-categories" element={<AdminPartnerCategoriesPage />} />
              <Route path="service-desk" element={<AdminServiceDeskPage />} />
            </Route>
          </Route>
          {/* Standalone Service Desk admin (outside CMS shell) */}
          <Route path="/service-desk/admin" element={<ServiceDeskAdminPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {!isAdminRoute && (
        <>
          <Footer />
          <WhatsAppButton />
          <ChatbotWidget />
        </>
      )}
    </div>
  );
}
